import { useState, useEffect } from 'react';
import { FileText, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/audit-log?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLogs(data);
    } catch (error) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'created':
        return 'bg-green-100 text-green-700';
      case 'updated':
      case 'edited':
        return 'bg-blue-100 text-blue-700';
      case 'approved':
        return 'bg-emerald-100 text-emerald-700';
      case 'manual_clock_in':
      case 'manual_clock_out':
        return 'bg-amber-100 text-amber-700';
      case 'deactivated':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-teal-600 text-xl">Loading audit log...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-emerald-900">Audit Log</h2>
          <p className="text-emerald-600 mt-1">Complete history of system changes and actions</p>
        </div>
        <Button
          data-testid="audit-refresh-button"
          onClick={fetchLogs}
          variant="outline"
          className="border-teal-500 text-teal-600 hover:bg-teal-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Audit Log Entries */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="audit-log-list">
        {logs.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">No audit log entries found</p>
          </div>
        ) : (
          <div className="divide-y divide-emerald-100">
            {logs.map((log, idx) => (
              <div
                key={log.id}
                data-testid={`audit-entry-${idx}`}
                className="p-6 hover:bg-emerald-50 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActionBadgeColor(log.action)}`} data-testid={`audit-action-${idx}`}>
                        {log.action}
                      </span>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold capitalize">
                        {log.entity_type}
                      </span>
                    </div>
                    <p className="text-emerald-900 font-medium mb-1">
                      <span className="font-semibold" data-testid={`audit-performer-${idx}`}>{log.performed_by}</span>
                      {' '}performed{' '}
                      <span className="font-semibold">{log.action}</span>
                      {' '}on{' '}
                      <span className="font-semibold capitalize">{log.entity_type}</span>
                    </p>
                    <p className="text-sm text-emerald-600">
                      Entity ID: <code className="bg-emerald-100 px-2 py-1 rounded">{log.entity_id}</code>
                    </p>
                    {log.ip_address && (
                      <p className="text-xs text-gray-500 mt-1">
                        IP: {log.ip_address}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-emerald-700 font-medium" data-testid={`audit-timestamp-${idx}`}>
                      {formatTimestamp(log.timestamp)}
                    </p>
                  </div>
                </div>

                {/* Show changes if available */}
                {(log.before || log.after) && (
                  <div className="mt-4 grid grid-cols-2 gap-4 text-xs">
                    {log.before && (
                      <div className="bg-red-50 p-3 rounded-lg">
                        <p className="font-semibold text-red-700 mb-2">Before:</p>
                        <pre className="text-red-600 overflow-x-auto">
                          {JSON.stringify(log.before, null, 2)}
                        </pre>
                      </div>
                    )}
                    {log.after && (
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="font-semibold text-green-700 mb-2">After:</p>
                        <pre className="text-green-600 overflow-x-auto">
                          {JSON.stringify(log.after, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
